import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LMUrlUtilService } from '@services/utils';
import { LMFormRequest } from '@form/index';
import { FormRequestType, LMDialogButtonKey, LMUniqueIdService } from '@app/utils';
import { Observable } from 'rxjs';
import { LMModalDialogWrapperRef, LMModalWrapperService } from "@app/services";
import { LMAddToMetricComputeDialogComponent } from "@field/busclassfield/lm-add-to-metric-compute-dialog.component";
import { LMFieldPickerSelectOptions } from "@modal/field-picker/lm-field-picker";

@Injectable()
export class LMBusclassFieldService {
  constructor(
    private http: HttpClient,
    private lmModalWrapperService: LMModalWrapperService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmUrlUtilService: LMUrlUtilService
  ) {
  }

  public makeRequest(
    busClass: string,
    lpl: string,
    isBusinessClassCompute: boolean,
    isBusClassTotal: boolean,
    computeType = '',
    dataView?: LMDataView,
    formResourceId?: string
  ): Observable<LMLplValidationResponse> {

    let requestType = isBusClassTotal
      ? FormRequestType.CubeRelValidateLPL
      : FormRequestType.ValidateCondition;

    if (computeType === 'metric-compute') {
      requestType = FormRequestType.ValidateMetricCompute;
    }

    if (computeType === 'metric-total') {
      // BusinessMetricTotals will be validated in the validateConditions function
      // in FormControllerAction.java
      requestType = FormRequestType.ValidateCondition;
    }

    const formRequest: LMFormRequest = {
      requestType,
      param1: busClass,
      param2: lpl,
      isBusinessClassCompute,
      computeType,
    };

    if (dataView) {
      formRequest.dataView = dataView;
      formRequest.formResourceId = formResourceId;
    }

    const lplValidationUrl = this.buildRequestUrl(busClass);
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http.post<LMLplValidationResponse>(lplValidationUrl, formRequest, {headers});
  }

  private buildRequestUrl(businessClass: string): string {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    let url = `${baseUrl}/form/${businessClass}`;
    url = this.lmUrlUtilService.propagateSessionKeys(url, '?');
    return url;
  }

  public openAddToMetricDialog(
      type: LMAddToMetricComputeType,
      selectListUrl: string,
      dataView: LMDataView,
      measureListUrl: string,
      measurePickerOptions: LMFieldPickerSelectOptions,
      disallowMeasureOverride: boolean
  ): Observable<LMAddToMetricComputeResult> {
    return new Observable<LMAddToMetricComputeResult>(observer => {
      let dialogComponent: LMAddToMetricComputeDialogComponent;
      // eslint-disable-next-line prefer-const
      let dialog: LMModalDialogWrapperRef<LMAddToMetricComputeDialogComponent>;
      const dialogId = 'lm-add-to-metric-compute-modal';

      const attributes: SohoAttributesArray = [{
        name: 'data-automation-id',
        value: dialogId
      }];

      const sohoModalOptions: SohoModalOptions = {
        fullsize: 'responsive',
        attributes
      };

      let title: string;
      switch (type) {
        case 'ADD_RATIO':
          title = Soho.Locale.translate('AddRatio');
          break;
        case 'ADD_VARIANCE_AMOUNT':
          title = Soho.Locale.translate('AddVarianceAmount');
          break;
        case 'ADD_VARIANCE_PERCENT':
          title = Soho.Locale.translate('AddVariancePercent');
          break;
        default:
          title = `invalid compute type: ${type}`; // Soho.Locale.translate('AddVariancePercent');
          break;
      }

      const buttons = [
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            const result: LMAddToMetricComputeResult = {
              action: 'Cancel',
              metric1: undefined,
              metric2: undefined,
              measure1: undefined,
              measure2: undefined
            };
            dialog.close(result);
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          click: () => {
            const metric1 = dialogComponent?.metricValue1;
            const metric2 = dialogComponent?.metricValue2;
            const measure1 = dialogComponent?.measureValue1;
            const measure2 = dialogComponent?.measureValue2;

            dialog.close({
              action: 'Ok',
              metric1: metric1,
              metric2: metric2,
              measure1: measure1,
              measure2: measure2
            } as LMAddToMetricComputeResult);
          },
          validate: false,
          isDefault: true
        }
      ];
      dialog = this.lmModalWrapperService
        .modal(LMAddToMetricComputeDialogComponent, sohoModalOptions)
        .buttons(buttons)
        .title(title)
        .isAlert(false)
        .open()
        .apply((pickerComponent: LMAddToMetricComputeDialogComponent) => {
          dialogComponent = pickerComponent;
          pickerComponent.type = type;
          pickerComponent.sourceField = 'BusinessMetric';
          pickerComponent.dataView = dataView;
          pickerComponent.metricSelectUrl = selectListUrl;
          pickerComponent.measureSelectUrl = measureListUrl;
          pickerComponent.measurePickerOptions = measurePickerOptions;
          pickerComponent.disallowMeasureOverride = disallowMeasureOverride;
        })
        .afterClose((result: LMAddToMetricComputeResult) => {
          if (!result) {
            result = { action: 'Cancel', metric1: undefined, metric2: undefined, measure1: undefined, measure2: undefined };
          }
          observer.next(result);
          observer.complete();
        });
    });
  }

  buildAddToMetricString(type: LMAddToMetricComputeType, result: LMAddToMetricComputeResult): string {
    let operand1 = result.metric1;
    if (result.measure1)
      operand1 += `.${result.measure1}`;

    let operand2 = result.metric2;
    if (result.measure2)
      operand2 += `.${result.measure2}`;

    let appendText = '';
    switch (type) {
      case 'ADD_RATIO':
        appendText = `[${operand1}] / [${operand2}]`;
        break;
      case 'ADD_VARIANCE_AMOUNT':
        appendText = `[${operand1}] - [${operand2}]`;
        break;
      case 'ADD_VARIANCE_PERCENT':
        appendText = `([${operand1}] - [${operand2}]) / [${operand2}]`;
        break;
    }
    return appendText;
  }
}
